import { api } from '@/api/client';
import type {
  CustomerPerformance,
  DashboardKpis,
  LanePerformance,
  TrendPoint,
} from '@/types/generated';

/**
 * The dashboard's data.
 *
 * NO CURRENCY PARAMETER. The reporting currency is the organisation's, decided server-side, so two
 * people in the same room cannot be looking at different totals for the same page. Every response
 * carries the currency it used and an `as_of` saying how old the aggregated half is.
 */

export interface ReportMeta {
  period: ReportPeriodValue;
  period_label: string;
  currency: string;
  /** When the materialized views were last rebuilt. Null means never. */
  as_of: string | null;
  /** Views whose last refresh failed — panels the response cannot vouch for. */
  stale_views: string[];
  /** Rows left out of the money figures because no exchange rate could convert them. */
  unconverted_quotes: number;
}

/**
 * A closed set, matching the server's. Arbitrary date ranges are deliberately not offered — see
 * ReportPeriod on the backend for why.
 */
export type ReportPeriodValue = '3m' | '6m' | '12m' | '24m';

export const REPORT_PERIODS: { value: ReportPeriodValue; label: string }[] = [
  { value: '3m', label: '3 months' },
  { value: '6m', label: '6 months' },
  { value: '12m', label: '12 months' },
  { value: '24m', label: '24 months' },
];

export interface DashboardResponse {
  kpis: DashboardKpis;
  trend: TrendPoint[];
  meta: ReportMeta;
}

export async function fetchDashboard(period: ReportPeriodValue): Promise<DashboardResponse> {
  const { data } = await api.get<DashboardResponse>('/reports/dashboard', { params: { period } });
  return data;
}

export interface LaneResponse {
  data: LanePerformance[];
  meta: ReportMeta & { unmapped_count: number };
}

export async function fetchLanes(limit = 25): Promise<LaneResponse> {
  const { data } = await api.get<LaneResponse>('/reports/lanes', { params: { limit } });
  return data;
}

export interface CustomerResponse {
  data: CustomerPerformance[];
  meta: ReportMeta;
}

export async function fetchCustomers(limit = 15): Promise<CustomerResponse> {
  const { data } = await api.get<CustomerResponse>('/reports/customers', { params: { limit } });
  return data;
}

/**
 * Basis points to a percentage.
 *
 * Every rate on the wire is an integer in basis points, for the same reason money is an integer
 * in minor units: a float that round-trips differently is a figure that changes when nothing did.
 * Division happens once, here, at the edge where it becomes a label.
 */
export function bpsToPercent(bps: number | null): number | null {
  return bps === null ? null : bps / 100;
}
